import {
    ChartPointObject,
    ChartPoints,
    HighChartsSerieDatetimeValue,
    ScatterPoints,
    SharedChartPointObject,
    SharedChartPoints,
} from 'adapter/in/common/components/widgets/Chart/types';
import { DateFormatter } from 'adapter/in/common/components/widgets/utils/dateFormatter';
import { numberFormatter } from 'adapter/in/common/utils/numberFormatter';
import { Pattern, PatternOrColor } from 'adapter/out/client/widgets/data/mappers/ChartMapper';
import i18n from 'asset/translation';

export const findComparisonDateIfAvailable = (series: ChartPointObject[], serieName: string, index: number) =>
    findComparisonSerie(series, serieName)?.data?.[index]?.unmodifiedX;

export const findComparisonSerie = (series: ChartPointObject[], serieName: string) =>
    series.find((serie) => serie.name.startsWith(serieName) && serie.name.length > serieName.length && serie.userOptions.comparison);

export const findNotComparisonSerie = (series: ChartPointObject[], serieName: string) =>
    series.find((serie) => serieName.startsWith(serie.name) && serie.name.length < serieName.length && !serie.userOptions.comparison);

export const tooltipFormatter = (
    points: ChartPoints | SharedChartPoints | ScatterPoints,
    sharedTooltip: boolean,
    isScatterTooltip: boolean,
    dateFormat: string,
    hasTitle: boolean,
    areXAxesCategorized: boolean,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    if (sharedTooltip) {
        return sharedTooltipFormatter(points as SharedChartPoints, dateFormat, thousandSeparator, decimalSeparator);
    }
    if (areXAxesCategorized) {
        return categorizedTooltipFormatter(points as ChartPoints, thousandSeparator, decimalSeparator);
    }
    if (isScatterTooltip) {
        return scatterTooltipFormatter(points as ScatterPoints, dateFormat, thousandSeparator, decimalSeparator);
    }
    return splittedTooltipFormatter(points as ChartPoints, dateFormat, hasTitle, thousandSeparator, decimalSeparator);
};

const categorizedTooltipFormatter = (points: ChartPoints, thousandSeparator: string, decimalSeparator: string) =>
    `<span style="color:${getColor(points.color)}">\u25CF </span>${points.series.name}` +
    `: ` +
    `<span style="font-weight:bold">${numberFormatter(points.y.toString(), decimalSeparator, thousandSeparator)} ${
        points.series.options.tooltip?.valueSuffix
    }</span><br/>`;

const findSharedComparisonDateIfAvailable = (points: SharedChartPointObject[]) => {
    const foundUnmodifiedX = points.find((value) => ((value.point as unknown) as HighChartsSerieDatetimeValue).unmodifiedX !== undefined);
    return foundUnmodifiedX ? ((foundUnmodifiedX.point as unknown) as HighChartsSerieDatetimeValue).unmodifiedX : undefined;
};

export const sharedTooltipFormatter = (
    points: SharedChartPoints,
    dateFormat: string,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    const comparisonDate = findSharedComparisonDateIfAvailable(points.points);
    const titleBeginning = `${new DateFormatter(dateFormat).dh(new Date(points.points[0].x))}`;
    const titleEnding = comparisonDate
        ? `${i18n.t('common.comparedTo').toLowerCase()} ${new DateFormatter(dateFormat).dh(comparisonDate)} <br/>`
        : '<br/>';

    return points.points.reduce(
        (s: string, point: SharedChartPointObject) =>
            `${s}<span style="color:${point.color}">\u25CF </span>${point.series.name}` +
            `: ` +
            `<span style="font-weight:bold">${point.y ? numberFormatter(point.y.toString(), decimalSeparator, thousandSeparator) : 'N/A'} ${
                point.series.options.tooltip?.valueSuffix
            }</span><br/>`,

        `${titleBeginning} ${titleEnding}`
    );
};

export const scatterTooltipFormatter = (
    points: ScatterPoints,
    dateFormat: string,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    const series = points.series;
    const index = points.series.xData.indexOf(points.x);
    const unitsX = series.xAxis.userOptions.labels.format.replace('{value} ', '') ?? 'N/A';
    const unitsY = series.yAxis.userOptions.labels.format.replace('{value} ', '') ?? 'N/A';
    const color = points.color;
    const name = series.name;
    const valueX = `${
        series.xData[index] !== undefined && series.xData[index] !== null
            ? numberFormatter(series.xData[index].toString(), decimalSeparator, thousandSeparator)
            : 'N/A'
    } ${unitsX}`;
    const valueY = `${
        series.yData[index] !== undefined && series.yData[index] !== null
            ? numberFormatter(series.yData[index].toString(), decimalSeparator, thousandSeparator)
            : 'N/A'
    } ${unitsY}`;
    return `<span><span style="color:${color}">\u25CF </span><span style="font-weight:bold;">${valueX} | ${valueY}</span> ${new DateFormatter(
        dateFormat
    ).dh(series.data[index].t!)}</span><br>${name}</span>`;
};

const buildTooltipSerie = (
    serie: {
        isHighlighted: boolean;
        color: string;
        name: string;
        value: string;
        valueStyle: string;
    },
    comparisonSerie?: { value: string; valueStyle: string }
) =>
    `<span style="opacity: ${serie.isHighlighted ? 1 : 0.5}"><span style="color:${serie.color}">\u25CF </span>${
        serie.name
    }: <span style="font-weight:bold;${serie.valueStyle}">${serie.value}</span>${
        comparisonSerie ? ` | <span style="font-weight:bold;${comparisonSerie.valueStyle}">${comparisonSerie.value}</span>` : ''
    }</span><br/>`;

export const splittedTooltipFormatter = (
    points: ChartPoints,
    dateFormat: string,
    hasTitle: boolean,
    thousandSeparator: string,
    decimalSeparator: string
): string => {
    const index = points.series.xData.indexOf(points.x);
    const serieName = points.series.name;
    let comparisonDate = points.point.unmodifiedX;
    const series = points.series.chart.series;
    let title = '';
    if (hasTitle) {
        if (!comparisonDate) {
            comparisonDate = findComparisonDateIfAvailable(series, serieName, index);
        }
        title += `${new DateFormatter(dateFormat).dh(new Date(points.point.x))}`;
        if (comparisonDate) {
            title = `${title} ${i18n.t('common.comparedTo').toLowerCase()} ${new DateFormatter(dateFormat).dh(comparisonDate)}`;
        }
        title += ' <br/>';
    }

    const defaultValue = 'N/A';

    const validColor = (color: unknown) => typeof color === 'string';
    return series
        .filter((serie) => serie.visible && serie.userOptions.enableMouseTracking)
        .reduce((s: string, serie: ChartPointObject) => {
            const noComparison = findNotComparisonSerie(series, serie.name);
            const isNoComparison = noComparison && noComparison.visible === false;
            if (!serie.userOptions.comparison || (serie.userOptions.comparison && !noComparison) || isNoComparison) {
                const isSelectedSerieComparison = serieName.startsWith(serie.name) && serie.name.length < serieName.length;
                const comparisonSerie = noComparison ?? findComparisonSerie(series, serie.name);
                const isHighlighted = (points.x === serie.xData[index] && points.y === serie.yData[index]) || isSelectedSerieComparison;

                serie.points.forEach((_point) => {
                    if (_point?.setState) _point?.setState('');
                });

                comparisonSerie?.points.forEach((_point) => {
                    if (_point?.setState) _point?.setState('');
                });

                if (isHighlighted) {
                    if (serie.visible) {
                        if (serie.setState) serie.setState('hover');

                        const currentPoint = serie.points.find((_point) => _point.y !== null && _point.x === points.series.xData[index]);
                        if (currentPoint && currentPoint.setState) {
                            currentPoint.setState('hover');
                        }
                    }

                    if (comparisonSerie?.visible) {
                        if (comparisonSerie?.setState) {
                            comparisonSerie.setState('hover');
                        }

                        const currentComparisonPoint = comparisonSerie?.points.find(
                            (_point) => _point.y !== null && _point.x === points.series.xData[index]
                        );
                        if (currentComparisonPoint?.setState) {
                            currentComparisonPoint.setState('hover');
                        }
                    }
                }

                const units = serie.options.tooltip?.valueSuffix ?? defaultValue;
                const value = `${
                    serie.yData[index] !== undefined && serie.yData[index] !== null
                        ? numberFormatter(serie.yData[index].toString(), decimalSeparator, thousandSeparator)
                        : defaultValue
                } ${units}`;
                const valueStyle = !isSelectedSerieComparison && isHighlighted ? ' text-decoration: underline;' : '';
                const color = (serie.points[index]?.color as string) ?? (serie.color as string);
                const comparisonColor = comparisonSerie?.points[index]?.color && getColor(comparisonSerie.points[index].color);
                const name = noComparison ? noComparison.name : serie.name;
                const comparisonValue = `${
                    comparisonSerie?.yData[index] !== undefined && comparisonSerie?.yData[index] !== null
                        ? numberFormatter(comparisonSerie.yData[index].toString(), decimalSeparator, thousandSeparator)
                        : defaultValue
                } ${units}`;
                const comparisonValueStyle = isSelectedSerieComparison && isHighlighted ? ' text-decoration: underline;' : '';
                const tooltipSerie = buildTooltipSerie(
                    {
                        isHighlighted,
                        color: !isSelectedSerieComparison && validColor(color) ? color : comparisonColor ?? '#000000',
                        name,
                        value: !isNoComparison ? value : comparisonValue,
                        valueStyle: !isNoComparison ? valueStyle : comparisonValueStyle,
                    },
                    comparisonSerie
                        ? {
                              value: isNoComparison ? value : comparisonValue,
                              valueStyle: isNoComparison ? valueStyle : comparisonValueStyle,
                          }
                        : undefined
                );
                return `${s}${tooltipSerie}`;
            }
            return s;
        }, title);
};

const getColor = (color: PatternOrColor) =>
    (color as Pattern).pattern !== undefined ? (color as Pattern).pattern.color : (color as string);
